import $http from '@vue-util/http';
import { Cookie } from '@vue-util/cookie';
import { LOYALTY_ENDPOINT } from '#shared/endpoints';
import { createHeaders } from '@/utils/CreateHeaders';
import { JOIN_STAR_REWARD_SLIDEOUT_TOGGLE_ID, STAR_REWARDS_PHONE_EXISTS, STAR_REWARDS_PHONE_INVALID } from '#shared/constants';
import { useSlideout } from '@/composables/useSlideout';
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';

const fallbackMessage =
  "Something went wrong. You can try again or call Macy's Customer Service at 1-800-289-6229. Don't forget, you can always return in store for free.";

const handleSetStarRewardsException = (store, error = {}) => {
  const status = error.response?.status;
  const err = error.response?.data?.errors?.error?.[0];
  const code = err?.errorCode;

  if (status === 503)
    return store.STAR_REWARDS_ERROR({
      message: fallbackMessage,
      errorCode: '',
    });

  if (code) {
    let message = err.message;
    if (code == 45028) message = STAR_REWARDS_PHONE_EXISTS;
    else if (code == 45208) message = STAR_REWARDS_PHONE_INVALID;
    else if (!message) message = "We're sorry, your session has timed out. Please <a href='/account/signin'>sign in</a> to your account again.";
    return store.STAR_REWARDS_ERROR({ message, errorCode: code });
  }

  return store.STAR_REWARDS_ERROR({ message: fallbackMessage, errorCode: '' });
};

export const useJoinStarRewardsStore = defineStore('joinStarRewardsStore', {
  state: () => ({
    starRewardsSummary: { loading: false, data: null, error: null },
    starReward: { loading: false, data: null, error: null, errorCode: null },
  }),
  actions: {
    STAR_REWARDS_LOADING(flag) {
      this.starReward = {
        loading: flag,
        data: null,
        error: null,
        errorCode: null,
      };
    },
    STAR_REWARDS(data) {
      this.starReward = {
        loading: false,
        data: { ...data },
        error: null,
        errorCode: null,
      };
    },
    STAR_REWARDS_ERROR({ message = null, errorCode = null } = {}) {
      this.starReward = {
        loading: false,
        data: null,
        error: message,
        errorCode,
      };
    },
    STAR_REWARDS_SUMMARY_LOADING(flag) {
      this.starRewardsSummary = { loading: flag, data: null, error: null };
    },
    STAR_REWARDS_SUMMARY(data) {
      this.starRewardsSummary = {
        loading: false,
        data: { ...data },
        error: null,
      };
    },
    STAR_REWARDS_SUMMARY_ERROR(error) {
      this.starRewardsSummary = { loading: false, data: null, error };
    },
    async SET_STAR_REWARDS({ phone, summary }) {
      try {
        this.STAR_REWARDS_LOADING(true);
        const payload = {
          loyalty: {
            phone,
            dobDate: summary?.birthDate || '',
            dobMonth: summary?.birthMonth || '',
            enrolled: false,
            enrollmentType: 'TENDER_NEUTRAL',
          },
        };
        const headers = createHeaders({ isUOP: false });
        if (headers.headers['X-Macys-UID']) delete headers.headers['X-Macys-UID'];
        const response = await $http.post(`${LOYALTY_ENDPOINT}/starrewardsenrollment?_origin=returns`, payload, headers);

        const err = response.data?.errors?.error?.[0];
        if (err?.errorCode)
          return handleSetStarRewardsException(this, {
            response: { data: response.data },
          });

        // Success
        const slideout = useSlideout();
        slideout.toggleSlideout(JOIN_STAR_REWARD_SLIDEOUT_TOGGLE_ID);
        this.STAR_REWARDS(response.data);
        this.FETCH_STAR_REWARDS_SUMMARY();
        useReturnItemStore().SET_HAS_ENROLLED_FOR_STARREWARDS(true);
      } catch (error) {
        handleSetStarRewardsException(this, error);
      }
    },
    async FETCH_STAR_REWARDS_SUMMARY(setDetails = true) {
      try {
        this.STAR_REWARDS_SUMMARY_LOADING(true);
        const headers = createHeaders({ isUOP: false });
        if (headers.headers['X-Macys-UID']) delete headers.headers['X-Macys-UID'];
        const response = await $http.get(`${LOYALTY_ENDPOINT}/starrewardssummary?_origin=returns`, headers);
        if (response.data?.encryptedLoyaltyId && setDetails) {
          const onlineUID = Cookie.get('macys_online_uid');
          localStorage.setItem(`hnfsr_${onlineUID}`, JSON.stringify(response.data));
        }
        this.STAR_REWARDS_SUMMARY(response.data || {});
      } catch {
        this.STAR_REWARDS_SUMMARY_ERROR('error message');
      }
    },
    async RESET_STAR_REWARDS() {
      this.STAR_REWARDS_ERROR();
    },
  },
});
